/**
 * NRS.01 D#5 — Clinical Sanity Check Ranges (Configurable)
 *
 * Physically possible ranges for biomarker values. These are NOT clinical
 * normal/optimal ranges — they catch OCR garbage, data corruption, and
 * extraction bugs where a value is physiologically impossible.
 *
 * Example: Hemoglobin of 300 g/dL is physically impossible (max ~25 in extreme
 * polycythemia). A value outside these ranges indicates data corruption, not a
 * sick patient.
 *
 * To add a new biomarker: append to CLINICAL_SANITY_RANGES below.
 * To adjust a threshold: update the min/max for the biomarker.
 * Ranges are intentionally wide — false positives here trigger operator review.
 */

import type { SanityCheckRange } from '../types/captainResultListener';

/**
 * Configurable clinical sanity check ranges.
 * Each entry defines the absolute physiological limits for a biomarker.
 * Values outside these ranges are flagged as implausible.
 */
export const CLINICAL_SANITY_RANGES: readonly SanityCheckRange[] = [
  // ── Hematology ──
  { biomarkerName: 'Hemoglobin', min: 0, max: 30, unit: 'g/dL' },
  { biomarkerName: 'Hematocrit', min: 0, max: 80, unit: '%' },
  { biomarkerName: 'WBC', min: 0, max: 500, unit: '10^3/uL' },
  { biomarkerName: 'RBC', min: 0, max: 10, unit: '10^6/uL' },
  { biomarkerName: 'Platelets', min: 0, max: 2000, unit: '10^3/uL' },
  { biomarkerName: 'MCV', min: 0, max: 200, unit: 'fL' },

  // ── Thyroid ──
  { biomarkerName: 'TSH', min: 0, max: 100, unit: 'mIU/L' },
  { biomarkerName: 'Free T4', min: 0, max: 20, unit: 'ng/dL' },
  { biomarkerName: 'Free T3', min: 0, max: 30, unit: 'pg/mL' },

  // ── Vitamins & Minerals ──
  { biomarkerName: 'Vitamin D', min: 0, max: 200, unit: 'ng/mL' },
  { biomarkerName: 'B12', min: 0, max: 10000, unit: 'pg/mL' },
  { biomarkerName: 'Folate', min: 0, max: 100, unit: 'ng/mL' },
  { biomarkerName: 'Iron', min: 0, max: 1000, unit: 'mcg/dL' },
  { biomarkerName: 'Ferritin', min: 0, max: 5000, unit: 'ng/mL' },
  { biomarkerName: 'Calcium', min: 0, max: 20, unit: 'mg/dL' },
  { biomarkerName: 'Magnesium', min: 0, max: 10, unit: 'mg/dL' },

  // ── Metabolic ──
  { biomarkerName: 'Glucose', min: 0, max: 1000, unit: 'mg/dL' },
  { biomarkerName: 'HbA1c', min: 0, max: 20, unit: '%' },
  { biomarkerName: 'Insulin', min: 0, max: 500, unit: 'uIU/mL' },
  { biomarkerName: 'Creatinine', min: 0, max: 30, unit: 'mg/dL' },
  { biomarkerName: 'BUN', min: 0, max: 300, unit: 'mg/dL' },
  { biomarkerName: 'eGFR', min: 0, max: 200, unit: 'mL/min/1.73m2' },
  { biomarkerName: 'Uric Acid', min: 0, max: 30, unit: 'mg/dL' },

  // ── Lipids ──
  { biomarkerName: 'Total Cholesterol', min: 0, max: 1000, unit: 'mg/dL' },
  { biomarkerName: 'LDL', min: 0, max: 500, unit: 'mg/dL' },
  { biomarkerName: 'HDL', min: 0, max: 200, unit: 'mg/dL' },
  { biomarkerName: 'Triglycerides', min: 0, max: 5000, unit: 'mg/dL' },

  // ── Liver ──
  { biomarkerName: 'ALT', min: 0, max: 10000, unit: 'U/L' },
  { biomarkerName: 'AST', min: 0, max: 10000, unit: 'U/L' },
  { biomarkerName: 'ALP', min: 0, max: 5000, unit: 'U/L' },
  { biomarkerName: 'GGT', min: 0, max: 5000, unit: 'U/L' },
  { biomarkerName: 'Bilirubin', min: 0, max: 50, unit: 'mg/dL' },
  { biomarkerName: 'Albumin', min: 0, max: 10, unit: 'g/dL' },

  // ── Hormones ──
  { biomarkerName: 'Testosterone', min: 0, max: 5000, unit: 'ng/dL' },
  { biomarkerName: 'Estradiol', min: 0, max: 10000, unit: 'pg/mL' },
  { biomarkerName: 'DHEA-S', min: 0, max: 1500, unit: 'mcg/dL' },
  { biomarkerName: 'Cortisol', min: 0, max: 100, unit: 'mcg/dL' },
  { biomarkerName: 'Progesterone', min: 0, max: 50, unit: 'ng/mL' },

  // ── Inflammation ──
  { biomarkerName: 'CRP', min: 0, max: 500, unit: 'mg/L' },
  { biomarkerName: 'ESR', min: 0, max: 200, unit: 'mm/hr' },
  { biomarkerName: 'Homocysteine', min: 0, max: 100, unit: 'umol/L' },

  // ── Electrolytes ──
  { biomarkerName: 'Sodium', min: 80, max: 200, unit: 'mEq/L' },
  { biomarkerName: 'Potassium', min: 0, max: 15, unit: 'mEq/L' },
  { biomarkerName: 'Chloride', min: 50, max: 150, unit: 'mEq/L' },
] as const;
